/**
 * Money / number formatting for the runtime.
 *
 * Accepts either:
 *   - a cent-like integer (e.g. 1990 = 19.90)
 *   - a float amount (e.g. 19.9)
 *   - a `MoneyV2`-ish object `{ amount, currencyCode }`
 *   - a string amount (e.g. "19.90") when combined with `currencyCode` opt.
 *
 * If `config.useBrowserFormatter` is true we use `Intl.NumberFormat` with the
 * current locale; otherwise we fall back to a deterministic "19,90 €" style
 * formatter so the rendered HTML matches snapshots across environments.
 */

import { config } from "../config.js";
import { getCurrentLocale } from "../i18n/locale.js";
import type { MoneyV2 } from "../types.js";

// ---------------------------------------------------------------------------
//  Symbol map for the manual fallback formatter
// ---------------------------------------------------------------------------

const SYMBOLS: Record<string, string> = {
  EUR: "\u20AC", // €
  USD: "$",
  GBP: "\u00A3", // £
  CHF: "CHF",
  CAD: "C$",
  JPY: "\u00A5", // ¥
};

function symbolFor(code: string): string {
  return SYMBOLS[code.toUpperCase()] ?? code.toUpperCase();
}

// ---------------------------------------------------------------------------
//  Input normalization
// ---------------------------------------------------------------------------

interface NormalizedMoney {
  amount: number;
  currencyCode: string;
}

export type MoneyInput =
  | number
  | string
  | MoneyV2
  | { amount: number | string; currencyCode?: string | null };

function normalize(
  input: MoneyInput,
  fallbackCurrency: string,
): NormalizedMoney {
  if (typeof input === "number") {
    // Heuristic: values >= 1000 and integer-looking → treat as cents.
    const asCents =
      Number.isInteger(input) && Math.abs(input) >= 1000 ? input / 100 : input;
    return { amount: asCents, currencyCode: fallbackCurrency };
  }

  if (typeof input === "string") {
    const n = parseFloat(input);
    return {
      amount: Number.isFinite(n) ? n : 0,
      currencyCode: fallbackCurrency,
    };
  }

  if (input && typeof input === "object") {
    const rawAmount = input.amount;
    const amt =
      typeof rawAmount === "number"
        ? rawAmount
        : typeof rawAmount === "string"
          ? parseFloat(rawAmount)
          : NaN;
    const cc =
      "currencyCode" in input && typeof input.currencyCode === "string"
        ? input.currencyCode
        : fallbackCurrency;
    return {
      amount: Number.isFinite(amt) ? amt : 0,
      currencyCode: cc,
    };
  }

  return { amount: 0, currencyCode: fallbackCurrency };
}

// ---------------------------------------------------------------------------
//  Formatters
// ---------------------------------------------------------------------------

function formatWithIntl(
  amount: number,
  currencyCode: string,
  locale: string,
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return formatFallback(amount, currencyCode);
  }
}

function formatFallback(amount: number, currencyCode: string): string {
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  // "19,90" style (FR-like, matches LMDR's locale).
  const fixed = abs.toFixed(2).replace(".", ",");
  return `${sign}${fixed}\u00A0${symbolFor(currencyCode)}`;
}

// ---------------------------------------------------------------------------
//  Public API
// ---------------------------------------------------------------------------

export function formatMoney(
  input: MoneyInput,
  opts: { currencyCode?: string; locale?: string } = {},
): string {
  const fallbackCC = opts.currencyCode ?? "EUR";
  const { amount, currencyCode } = normalize(input, fallbackCC);
  const effectiveCC =
    (opts.currencyCode ?? currencyCode ?? "EUR").toUpperCase() || "EUR";

  if (config.useBrowserFormatter) {
    const locale = opts.locale ?? browserLocale();
    return formatWithIntl(amount, effectiveCC, locale);
  }
  return formatFallback(amount, effectiveCC);
}

function browserLocale(): string {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  const lang = getCurrentLocale().toLowerCase();
  return `${lang}-${lang.toUpperCase()}`;
}
